import { Pressable, Text, View, type PressableProps } from 'react-native'
import { useTranslation } from 'react-i18next'

import { KycDiagnostics } from '@/utils/kycDiagnostics'
import { Button } from '@/components/base'
import { Play } from '@/components/icon'

export interface RecordingSectionProps {
  videoUri: string
  addVideo: (uri: string, blob?: Blob) => void
  deleteVideo: () => void
  diagnostics: KycDiagnostics
  onSubmit: () => void
  resetKey: () => void
}

export const MAX_RECORDING_DURATION_SECONDS = 15

export type RecordStatus = 'preparing' | 'recording' | 'processing' | 'waiting' | 'playing' | 'paused' | 'played'

export function RecordingInstructions({ userId }: { userId: string | undefined }) {
  const { t } = useTranslation()
  return (
    <View className={'items-center gap-2 p-4'}>
      <Text className={'text-sm text-text-secondary'}>
        {t('kyc:recordingInstructions')}
      </Text>
      <Text className={'font-roboto-semibold text-2xl text-text-info'}>{userId}</Text>
    </View>
  )
}

export function CameraPermissionMessage() {
  const { t } = useTranslation()
  return (
    <View className={'flex-1 items-center justify-center bg-black px-4'}>
      <Text className={'text-center text-white'}>{t('kyc:cameraPermissionRequired')}</Text>
    </View>
  )
}

interface PlayButtonProps extends PressableProps {
  isPlaying: boolean
}

export function PlayButton({ isPlaying, ...delegated }: PlayButtonProps) {
  return (
    <Pressable
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      {...delegated}
    >
      {!isPlaying && (
        <>
          <View className={'absolute inset-0 bg-black opacity-50'} />
          <Play className={'relative size-20 text-white'} />
        </>
      )}
    </Pressable>
  )
}

interface RecordButtonProps extends PressableProps {
  isRecording: boolean
}

function RecordButton({ isRecording, disabled, ...delegated }: RecordButtonProps) {
  return (
    <Pressable disabled={disabled} {...delegated}>
      {({ pressed }) => (
        <View
          className={'size-16 items-center justify-center rounded-[32px] border-2 border-danger'}
          style={{ opacity: disabled || pressed ? 0.3 : 1 }}
        >
          {isRecording ? (
            <View className={'size-6 rounded-sm bg-danger'} />
          ) : (
            <View className={'size-[52px] rounded-full bg-danger'} />
          )}
        </View>
      )}
    </Pressable>
  )
}

export function RecordingControls({
  status,
  time,
  cameraReady,
  onRecord,
  onReset,
  onSubmit
}: {
  status: RecordStatus
  time: number
  cameraReady: boolean
  onRecord: () => void
  onReset: () => void
  onSubmit: () => void
}) {
  const { t } = useTranslation()
  const isVideoShown = ['waiting', 'playing', 'paused', 'played'].includes(status)
  const isRecordButtonShown = ['preparing', 'recording'].includes(status)

  if (status === 'processing') {
    return (
      <View className={'items-center p-4'}>
        <Text className={'text-base font-medium'}>{t('kyc:processingVideo')}</Text>
      </View>
    )
  }

  return (
    <View className={'items-center p-4'} style={{ gap: isVideoShown ? 40 : 16 }}>
      {cameraReady && (
        <Text className={'text-base font-medium'}>
          {'00:' + Math.min(time, MAX_RECORDING_DURATION_SECONDS).toString().padStart(2, '0')}
        </Text>
      )}
      {cameraReady && isRecordButtonShown && <RecordButton onPress={onRecord} isRecording={status === 'recording'} />}
      {!isRecordButtonShown && (
        <View className={'flex-row gap-2 self-stretch'}>
          <Button className={'flex-1'} intent={'primary'} variant={'outline'} size={'md'} onPress={onReset}>
            {t('kyc:recordAgain')}
          </Button>
          <Button className={'flex-1'} intent={'primary'} variant={'solid'} size={'md'} onPress={onSubmit}>
            {t('kyc:submitForReview')}
          </Button>
        </View>
      )}
    </View>
  )
}
